import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../components/common/DecoratedHeader/DecoratedHeader';
import Loading from '../../components/common/Loading/Loading';
import { AppFormInput } from '../../components/common/appForms';
import Logo from '../../components/common/Logo/Logo';
import { FupService } from '../../services';
import type { StudentByLinkData } from '../../services/FupService';
import { getFupRoutePaths } from '../../config/hostApps';
import CheckoutContainer from '../Checkout/components/CheckoutContainer';
import pageStyles from '../Checkout/CheckoutPage.module.css';
import FupQuestionnaireFields from './FupQuestionnaireFields';
import {
  composeFupResponse,
  EMPTY_FUP_QUESTIONNAIRE,
  FUP_CLOSING_LINE,
  FUP_INTRO_LINES,
  validateFupQuestionnaire,
  type FupQuestionnaireAnswers,
  type FupQuestionnaireErrors,
} from './fupQuestionnaire';
import styles from './Fup.module.css';

type FeedbackState = {
  message: string;
  variant: 'success' | 'error';
} | null;

const isAlreadyAnsweredError = (message: string): boolean => (
  /já foi respondido/i.test(message)
);

const FupPublic: React.FC = () => {
  const { token, studentSlug } = useParams<{ token: string; studentSlug?: string }>();
  const navigate = useNavigate();
  const [studentData, setStudentData] = useState<StudentByLinkData | null>(null);
  const [loadingStudent, setLoadingStudent] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<FupQuestionnaireAnswers>(EMPTY_FUP_QUESTIONNAIRE);
  const [errors, setErrors] = useState<FupQuestionnaireErrors>({});
  const [feedbackState, setFeedbackState] = useState<FeedbackState>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const goToThankYou = useCallback((linkToken: string) => {
    navigate(getFupRoutePaths().thankYou(linkToken, studentSlug), { replace: true });
  }, [navigate, studentSlug]);

  const loadStudent = useCallback(async (linkToken: string) => {
    setLoadingStudent(true);
    setLoadError(null);

    try {
      const data = await FupService.getStudentByLink(linkToken);

      if (data.responded_at) {
        goToThankYou(linkToken);
        return;
      }

      setStudentData(data);
      setAnswers(EMPTY_FUP_QUESTIONNAIRE);
      setErrors({});
      setFeedbackState(null);
    } catch (error: unknown) {
      setStudentData(null);
      setAnswers(EMPTY_FUP_QUESTIONNAIRE);
      setLoadError(error instanceof Error ? error.message : 'Não foi possível carregar os dados da aluna.');
    } finally {
      setLoadingStudent(false);
    }
  }, [goToThankYou]);

  useEffect(() => {
    if (!token) {
      setLoadError('Link de FUP inválido.');
      setLoadingStudent(false);
      return;
    }

    void loadStudent(token);
  }, [loadStudent, token]);

  const handleAnswerChange = useCallback(<K extends keyof FupQuestionnaireAnswers>(
    field: K,
    value: FupQuestionnaireAnswers[K],
  ) => {
    setAnswers((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setFeedbackState(null);
  }, []);

  const handleOpenConfirmModal = useCallback(() => {
    if (!token || !studentData) {
      return;
    }

    const nextErrors = validateFupQuestionnaire(answers);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setShowConfirmModal(true);
  }, [answers, studentData, token]);

  const handleConfirmSubmit = useCallback(async () => {
    if (!token || !studentData) {
      return;
    }

    setIsSubmitting(true);
    setFeedbackState(null);

    try {
      await FupService.submitFeedbackByToken({
        token,
        studentFupResponse: composeFupResponse(answers),
        answers,
      });

      setShowConfirmModal(false);
      goToThankYou(token);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Não foi possível enviar o FUP agora.';

      if (isAlreadyAnsweredError(message)) {
        setShowConfirmModal(false);
        goToThankYou(token);
        return;
      }

      setFeedbackState({
        message,
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [answers, goToThankYou, studentData, token]);

  if (loadingStudent) {
    return <Loading fullscreen />;
  }

  return (
    <CheckoutContainer>
      <div className={styles.logoHeader}>
        <Logo className={styles.formLogo} />
        <DecoratedHeader
          className={styles.formTitle}
          title="Follow up do próximo ciclo"
        />
        <p className={pageStyles.headerSubtitle}>{FUP_INTRO_LINES[0]}</p>
      </div>

      {loadError && (
        <div className={styles.feedbackWrap}>
          <AlertBanner message={loadError} variant="error" />
        </div>
      )}

      {!loadError && studentData && (
        <div className={styles.centeredLayout}>
          <div className={styles.centeredCard}>
            <div className={styles.formWrapper}>
              {feedbackState && (
                <AlertBanner
                  message={feedbackState.message}
                  variant={feedbackState.variant}
                  className={styles.feedbackAlert}
                />
              )}

              <div className={styles.formGrid}>
                <AppFormInput
                  id="fup-student-name"
                  name="studentName"
                  label="Nome"
                  value={studentData.display_name ?? ''}
                  onChange={() => undefined}
                  readOnly
                />

                <FupQuestionnaireFields
                  answers={answers}
                  errors={errors}
                  disabled={isSubmitting}
                  autoFocus
                  onChange={handleAnswerChange}
                />
              </div>

              <p className={styles.closing}>{FUP_CLOSING_LINE}</p>

              <div className={styles.footerActions}>
                <Button
                  type="button"
                  variant="primary"
                  size="app"
                  fullWidth
                  onClick={handleOpenConfirmModal}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar feedback'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={showConfirmModal}
        title="Confirmar envio"
        confirmLabel="Confirmar envio"
        cancelLabel="Cancelar"
        variant="warning"
        loading={isSubmitting}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSubmit}
      >
        <p>Após enviar sua resposta, não será possível alterá-la posteriormente.</p>
        <p>Deseja realmente enviar seu FUP?</p>
      </ConfirmModal>
    </CheckoutContainer>
  );
};

export default FupPublic;
